import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import { NodeSelection } from 'prosemirror-state';
import { useContext, useState } from 'react';
import { useToast } from '../../../hooks/useToast/useToast';
import { CustomEditorContext } from '.';
import BoxedTextIcon from './_assets/boxed-text.svg';
import HrIcon from './_assets/hr.svg';
import type { ImageButtonProps } from './_components/ImageButton';
import { ImageButton } from './_components/ImageButton';
import { ImageEditButton } from './_components/ImageEditButton';
import { LinkButton } from './_components/LinkButton';
import { PreviewButton } from './_components/PreviewButton';
import { YouTubeButton } from './_components/YouTubeButton';
import { TEXT_ALIGN } from './_constants';
import type { CustomImageOptions } from './_utils/CustomImage';
import type { CustomLinkOptions } from './_utils/CustomLink';
import { extractAlignValue } from './_utils/extractAlignValue';
import { extractMaxWidthValue } from './_utils/extractMaxWidthValue';
import type { BizFontSizeType } from './_utils/FontSize';
import { BIZ_FONT_SIZES, DEFAULT_FONT_SIZE } from './_utils/FontSize';
import { FONT_WEIGHT_LABELS, FONT_WEIGHTS } from './_utils/FontWeight';
import { getYouTubeVideoId } from './_utils/getYouTubeVideoId';
import { HIGHLIGHT_LABELS, HIGHLIGHTS } from './_utils/Highlight';
import { TEXT_COLOR_LABELS, TEXT_COLORS } from './_utils/TextColor';
export interface ToolbarProps {
  imageUploadOptions?: ImageButtonProps;
}
const DEFAULT_CUSTOM_IMAGE_OPTIONS = {
  maxWidth: null,
  isBorderRadius: false,
  align: 'left'
} as const;
const DEFAULT_CUSTOM_LINK_OPTIONS = {
  theme: 'text',
  size: 'H56',
  isOutLink: false,
  href: ''
} as const;
export const BizToolbar = t0 => {
  const $ = _c(110);
  const {
    imageUploadOptions
  } = t0;
  const {
    toast,
    toastError
  } = useToast();
  const {
    editor
  } = useContext(CustomEditorContext);
  const isImageSelected = editor?.state.selection instanceof NodeSelection && editor?.state.selection.node.type.name === "image";
  const [customImageOptions, setCustomImageOptions] = useState(DEFAULT_CUSTOM_IMAGE_OPTIONS);
  const [customLinkOptions, setCustomLinkOptions] = useState(DEFAULT_CUSTOM_LINK_OPTIONS);
  const [youTubeUrl, setYouTubeUrl] = useState("");
  let t1;
  if ($[0] !== editor) {
    const getSelectedNodeAttrText = attrName => {
      if (!editor) {
        return "";
      }
      const selection = editor.state.selection;
      if (!(selection instanceof NodeSelection)) {
        return "";
      }
      const attrs = selection.node.attrs as Record<string, unknown>;
      const value = attrs[attrName];
      return typeof value === "string" ? value : "";
    };
    t1 = () => {
      const styleText = getSelectedNodeAttrText("style");
      const classText = getSelectedNodeAttrText("class");
      const maxWidth = extractMaxWidthValue(styleText);
      const isBorderRadius = classText.includes("rounded");
      const align = extractAlignValue(classText);
      setCustomImageOptions({
        maxWidth,
        isBorderRadius,
        align
      });
    };
    $[0] = editor;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const initialImageOptions = t1;
  let t2;
  if ($[2] !== isImageSelected) {
    t2 = () => {
      if (isImageSelected) {
        setCustomLinkOptions(_temp);
      }
    };
    $[2] = isImageSelected;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const initialLinkOptions = t2;
  if (!editor) {
    return null;
  }
  let t3;
  if ($[4] !== editor) {
    t3 = TEXT_COLORS.find(t4 => {
      const [, color] = t4;
      return editor.isActive("textStyle", {
        color
      });
    }) ?? [];
    $[4] = editor;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [selectedColor] = t3;
  let t4;
  if ($[6] !== editor) {
    t4 = FONT_WEIGHTS.find(fontWeight => editor.isActive("fontWeight", {
      fontWeight
    }));
    $[6] = editor;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const selectedFontWeight = t4;
  let t5;
  if ($[8] !== editor) {
    t5 = BIZ_FONT_SIZES.find(fontSize => editor.isActive("fontSize", {
      fontSize
    })) ?? DEFAULT_FONT_SIZE;
    $[8] = editor;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const selectedFontSize = t5;
  let isBoxedText;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[10] !== editor || $[11] !== selectedColor || $[12] !== selectedFontSize || $[13] !== selectedFontWeight) {
    isBoxedText = editor.isActive("boxedText");
    let t15;
    if ($[24] !== editor) {
      t15 = TEXT_ALIGN.find(textAlign => editor.isActive("textAlign", {
        textAlign
      }));
      $[24] = editor;
      $[25] = t15;
    } else {
      t15 = $[25];
    }
    const selectedTextAlign = t15;
    t9 = "flex flex-wrap items-center justify-center gap-8 bg-Gray-100 p-8";
    let t16;
    if ($[26] !== editor) {
      t16 = e => {
        editor.chain().focus().setBizFontSize(e.target.value as BizFontSizeType).run();
      };
      $[26] = editor;
      $[27] = t16;
    } else {
      t16 = $[27];
    }
    let t17;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = BIZ_FONT_SIZES.map(_temp2);
      $[28] = t17;
    } else {
      t17 = $[28];
    }
    if ($[29] !== selectedFontSize || $[30] !== t16) {
      t10 = <select aria-label={"\uAE00\uC790 \uD06C\uAE30"} value={selectedFontSize} onChange={t16}>{t17}</select>;
      $[29] = selectedFontSize;
      $[30] = t16;
      $[31] = t10;
    } else {
      t10 = $[31];
    }
    let t18;
    if ($[32] !== editor) {
      t18 = e_0 => {
        editor?.chain().focus().setTextAlign(e_0.target.value).run();
      };
      $[32] = editor;
      $[33] = t18;
    } else {
      t18 = $[33];
    }
    let t19;
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = TEXT_ALIGN.map(_temp3);
      $[34] = t19;
    } else {
      t19 = $[34];
    }
    if ($[35] !== selectedTextAlign || $[36] !== t18) {
      t11 = <select aria-label={"\uC815\uB82C"} value={selectedTextAlign} onChange={t18}>{t19}</select>;
      $[35] = selectedTextAlign;
      $[36] = t18;
      $[37] = t11;
    } else {
      t11 = $[37];
    }
    let t20;
    if ($[38] !== editor || $[39] !== selectedColor) {
      t20 = TEXT_COLORS.map(t21 => {
        const [label, color_0] = t21;
        return <button key={label} aria-label={`글자색 ${TEXT_COLOR_LABELS[label]}`} aria-pressed={selectedColor === label} className={cn("font-800", {
          underline: selectedColor === label
        })} style={{
          color: color_0
        }} type="button" onClick={() => editor?.chain().focus().setColor(color_0).run()}>A</button>;
      });
      $[38] = editor;
      $[39] = selectedColor;
      $[40] = t20;
    } else {
      t20 = $[40];
    }
    if ($[41] !== t20) {
      t12 = <section className="flex gap-8 rounded-xsmall border-1 border-Gray-600 px-8 py-2">{t20}</section>;
      $[41] = t20;
      $[42] = t12;
    } else {
      t12 = $[42];
    }
    let t21;
    if ($[43] !== editor || $[44] !== selectedFontWeight) {
      t21 = FONT_WEIGHTS.map(fontWeight_0 => <button key={fontWeight_0} aria-label={FONT_WEIGHT_LABELS[fontWeight_0]} aria-pressed={selectedFontWeight === fontWeight_0} className={cn(fontWeight_0, {
        underline: selectedFontWeight === fontWeight_0
      })} type="button" onClick={() => editor?.chain().focus().setFontWeight(fontWeight_0).run()}>B</button>);
      $[43] = editor;
      $[44] = selectedFontWeight;
      $[45] = t21;
    } else {
      t21 = $[45];
    }
    if ($[46] !== t21) {
      t13 = <section className="flex gap-8 rounded-xsmall border-1 border-Gray-600 px-8 py-2">{t21}</section>;
      $[46] = t21;
      $[47] = t13;
    } else {
      t13 = $[47];
    }
    if ($[48] !== editor) {
      t14 = HIGHLIGHTS.map(highlight => {
        const isActive = editor.isActive("highlight", {
          highlight
        });
        return <button key={highlight} aria-label={HIGHLIGHT_LABELS[highlight]} aria-pressed={isActive} className={cn(highlight, "flex size-24 min-h-24 min-w-24 items-center justify-center rounded-xsmall font-700", {
          "border-1 border-Gray-800": isActive
        })} type="button" onClick={() => isActive ? editor.chain().focus().unsetHighlight().run() : editor.chain().focus().setHighlight(highlight).run()} />;
      });
      $[48] = editor;
      $[49] = t14;
    } else {
      t14 = $[49];
    }
    t6 = "\uBC15\uC2A4\uD14D\uC2A4\uD2B8";
    t7 = isBoxedText;
    t8 = cn("box-border flex size-24 items-center justify-center", {
      "border-Gray-800 border-1 rounded-xxsmall": isBoxedText
    });
    $[10] = editor;
    $[11] = selectedColor;
    $[12] = selectedFontSize;
    $[13] = selectedFontWeight;
    $[14] = isBoxedText;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
    $[18] = t13;
    $[19] = t14;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    isBoxedText = $[14];
    t10 = $[15];
    t11 = $[16];
    t12 = $[17];
    t13 = $[18];
    t14 = $[19];
    t6 = $[20];
    t7 = $[21];
    t8 = $[22];
    t9 = $[23];
  }
  let t15;
  if ($[50] !== editor || $[51] !== isBoxedText) {
    t15 = () => isBoxedText ? editor.chain().focus().unsetBoxedText().run() : editor.chain().focus().setBoxedText().run();
    $[50] = editor;
    $[51] = isBoxedText;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  let t16;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <BoxedTextIcon />;
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  let t17;
  if ($[54] !== t15 || $[55] !== t6 || $[56] !== t7 || $[57] !== t8) {
    t17 = <button aria-label={t6} aria-pressed={t7} className={t8} type="button" onClick={t15}>{t16}</button>;
    $[54] = t15;
    $[55] = t6;
    $[56] = t7;
    $[57] = t8;
    $[58] = t17;
  } else {
    t17 = $[58];
  }
  let t18;
  if ($[59] !== editor) {
    t18 = () => {
      editor?.chain().focus().setHorizontalRule().run();
    };
    $[59] = editor;
    $[60] = t18;
  } else {
    t18 = $[60];
  }
  let t19;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <HrIcon />;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  let t20;
  if ($[62] !== t18) {
    t20 = <button aria-label={"\uAD6C\uBD84\uC120 \uC0BD\uC785"} type="button" onClick={t18}>{t19}</button>;
    $[62] = t18;
    $[63] = t20;
  } else {
    t20 = $[63];
  }
  let t21;
  if ($[64] !== imageUploadOptions) {
    t21 = <ImageButton {...imageUploadOptions} />;
    $[64] = imageUploadOptions;
    $[65] = t21;
  } else {
    t21 = $[65];
  }
  const t22 = !isImageSelected;
  let t23;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = align_0 => {
      setCustomImageOptions(prev_0 => ({
        ...prev_0,
        align: align_0
      }));
    };
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  let t24;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = isBorderRadius_0 => {
      setCustomImageOptions(prev_1 => ({
        ...prev_1,
        isBorderRadius: isBorderRadius_0
      }));
    };
    $[67] = t24;
  } else {
    t24 = $[67];
  }
  let t25;
  if ($[68] !== customImageOptions || $[69] !== editor) {
    t25 = () => {
      editor.chain().focus().setCustomImage(customImageOptions).run();
      setCustomImageOptions(DEFAULT_CUSTOM_IMAGE_OPTIONS);
    };
    $[68] = customImageOptions;
    $[69] = editor;
    $[70] = t25;
  } else {
    t25 = $[70];
  }
  let t26;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = maxWidth_0 => {
      setCustomImageOptions(prev_2 => ({
        ...prev_2,
        maxWidth: maxWidth_0
      }));
    };
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  let t27;
  if ($[72] !== customImageOptions || $[73] !== initialImageOptions || $[74] !== t22 || $[75] !== t25) {
    t27 = <ImageEditButton disabled={t22} selectedOptions={customImageOptions} onAlignChange={t23} onClick={initialImageOptions} onIsBorderRadiusChange={t24} onSubmit={t25} onWidthChange={t26} />;
    $[72] = customImageOptions;
    $[73] = initialImageOptions;
    $[74] = t22;
    $[75] = t25;
    $[76] = t27;
  } else {
    t27 = $[76];
  }
  let t28;
  if ($[77] !== editor || $[78] !== toast || $[79] !== toastError || $[80] !== youTubeUrl) {
    t28 = () => {
      if (youTubeUrl) {
        ;
        try {
          const videoId = getYouTubeVideoId(youTubeUrl);
          editor.chain().focus().setCustomYouTubeIframe({
            src: `https://www.youtube.com/embed/${videoId}`
          }).run();
          setYouTubeUrl("");
        } catch (t29) {
          const error = t29;
          if (error instanceof Error && error.message) {
            toast({
              type: "fail",
              message: error.message
            });
            return;
          }
          toastError({
            error
          });
        }
      }
    };
    $[77] = editor;
    $[78] = toast;
    $[79] = toastError;
    $[80] = youTubeUrl;
    $[81] = t28;
  } else {
    t28 = $[81];
  }
  let t29;
  if ($[82] !== t28 || $[83] !== youTubeUrl) {
    t29 = <YouTubeButton youTubeUrl={youTubeUrl} onSubmit={t28} onYouTubeUrlChange={setYouTubeUrl} />;
    $[82] = t28;
    $[83] = youTubeUrl;
    $[84] = t29;
  } else {
    t29 = $[84];
  }
  let t30;
  let t31;
  let t32;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = href => {
      setCustomLinkOptions(prev_3 => ({
        ...prev_3,
        href
      }));
    };
    t31 = isOutLink => {
      setCustomLinkOptions(prev_4 => ({
        ...prev_4,
        isOutLink
      }));
    };
    t32 = size => {
      setCustomLinkOptions(prev_5 => ({
        ...prev_5,
        size
      }));
    };
    $[85] = t30;
    $[86] = t31;
    $[87] = t32;
  } else {
    t30 = $[85];
    t31 = $[86];
    t32 = $[87];
  }
  let t33;
  if ($[88] !== customLinkOptions || $[89] !== editor) {
    t33 = () => {
      editor.chain().focus().setCustomLink(customLinkOptions).run();
      setCustomLinkOptions(DEFAULT_CUSTOM_LINK_OPTIONS);
    };
    $[88] = customLinkOptions;
    $[89] = editor;
    $[90] = t33;
  } else {
    t33 = $[90];
  }
  let t34;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = theme => {
      setCustomLinkOptions(prev_6 => ({
        ...prev_6,
        theme
      }));
    };
    $[91] = t34;
  } else {
    t34 = $[91];
  }
  let t35;
  if ($[92] !== customLinkOptions || $[93] !== initialLinkOptions || $[94] !== t33) {
    t35 = <LinkButton selectedOptions={customLinkOptions} onClick={initialLinkOptions} onHrefChange={t30} onIsOutLinkChange={t31} onSizeChange={t32} onSubmit={t33} onThemeChange={t34} />;
    $[92] = customLinkOptions;
    $[93] = initialLinkOptions;
    $[94] = t33;
    $[95] = t35;
  } else {
    t35 = $[95];
  }
  let t36;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <PreviewButton />;
    $[96] = t36;
  } else {
    t36 = $[96];
  }
  let t37;
  if ($[97] !== t10 || $[98] !== t11 || $[99] !== t12 || $[100] !== t13 || $[101] !== t14 || $[102] !== t17 || $[103] !== t20 || $[104] !== t21 || $[105] !== t27 || $[106] !== t29 || $[107] !== t35 || $[108] !== t9) {
    t37 = <nav className={t9}>{t10}{t11}{t12}{t13}{t14}{t17}{t20}{t21}{t27}{t29}{t35}{t36}</nav>;
    $[97] = t10;
    $[98] = t11;
    $[99] = t12;
    $[100] = t13;
    $[101] = t14;
    $[102] = t17;
    $[103] = t20;
    $[104] = t21;
    $[105] = t27;
    $[106] = t29;
    $[107] = t35;
    $[108] = t9;
    $[109] = t37;
  } else {
    t37 = $[109];
  }
  return t37;
};
function _temp(prev) {
  return {
    ...prev,
    theme: "image"
  };
}
function _temp2(fontSize_0) {
  return <option key={fontSize_0} className={fontSize_0} value={fontSize_0}>{fontSize_0}</option>;
}
function _temp3(textAlign_0) {
  return <option key={textAlign_0} value={textAlign_0}>{textAlign_0}</option>;
}